import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "account-creation__social-btn" }
const _hoisted_2 = ["innerHTML"]
const _hoisted_3 = { class: "password-constraints" }
const _hoisted_4 = { class: "account-creation__checkboxes" }
const _hoisted_5 = ["innerHTML"]

import debounce from 'lodash/debounce';
    import {computed, inject, reactive, ref, watch} from 'vue';
    import {useStore} from 'vuex';
    import {RouteRecordName, useRoute, useRouter} from 'vue-router';
    import {useVuelidate} from '@vuelidate/core';
    import {helpers, minLength, required, email} from '@vuelidate/validators';
    import {escapeHtml} from '@malt/sanitize';
    import type {EmailAddressValidityResponse} from '@/profile-creation-wizard/components/for-creation/ProfileCreationService';
    import {
        EmailAddressValidity,
        ProfileCreationError,
        ProfileCreationService,
    } from '@/profile-creation-wizard/components/for-creation/ProfileCreationService';
    import {DI} from '@/profile-creation-wizard/common/DependencyInjection';
    import Step from '@/profile-creation-wizard/common/ui/step/Step.vue';
    import creationGetters from '@/profile-creation-wizard/store/creation/getters';
    import ExistingProfileJoiningMaltStrategy from '@/profile-creation-wizard/components/for-creation/ui/ExistingProfileJoiningMaltStrategy.vue';
    import {useTranslation} from '@malt/vue3-use';
    import {useGlobalProperties} from '@/common/composable/useGlobalProperties';

    
export default /*@__PURE__*/_defineComponent({
  __name: 'AccountCreation',
  setup(__props) {

    const profileCreationService = inject(DI.profileCreationService) as ProfileCreationService;
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const {msg} = useTranslation();
    const {$features} = useGlobalProperties();

    const firstNameModel = ref('');
    const firstNameError = ref('');
    const lastNameModel = ref('');
    const lastNameError = ref('');
    const emailModel = ref('');
    const passwordModel = ref('');
    const optOutFromNewsletterModel = ref(false);
    const agreementModel = ref(false);
    const errorFormEmailAlreadyUsed = ref(false);
    const displayExistingProfileJoiningMaltStrategy = ref(false);

    const rules = {
        firstName: {
            required,
        },
        lastName: {
            required,
        },
        email: {
            required,
            email,
        },
        password: {
            required,
            minLength: minLength(8),
            pattern: helpers.regex(/^(?=.*[A-Z])(?=.*[\W_])/),
        },
    };
    const state = reactive({
        firstName: firstNameModel,
        lastName: lastNameModel,
        email: emailModel,
        password: passwordModel,
    });
    const v$ = useVuelidate(rules, state);

    const emailAddressAlreadyUsedReceivedViaParameters = ref('');
    const emailValidity = ref<EmailAddressValidity>('VALID_ADDRESS');

    const loading = ref(false);
    const isCompletelyValid = computed(() => {
        return !loading.value && !v$.value.$invalid && emailValidity.value !== 'UNCLAIMED_COMATCH_ACCOUNT';
    });
    const nextButtonClicked = ref(false);

    const passwordLengthValid = computed(() => passwordModel.value.length >= 8);
    const passwordUpperCaseValid = computed(() => /[A-Z]/.test(passwordModel.value));
    const passwordSpecialValid = computed(() => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(passwordModel.value));

    const resumptionDetails = store.getters[creationGetters.profileCreationResumptionDetails];

    if (!route.query['r']) {
        // this will allow the user - if they ever leave the account creation page - to go back in
        // their history and redisplay it without being redirected to the start of the workflow
        router.replace({query: {r: resumptionDetails.requestId}});
    }

    if (typeof route.query['account-already-exists'] === 'string') {
        emailAddressAlreadyUsedReceivedViaParameters.value = route.query['account-already-exists'];
    }

    async function getEmailAddressValidity(): Promise<EmailAddressValidityResponse | void> {
        const [data] = await profileCreationService.getEmailAddressValidity(emailModel.value);
        if (data) {
            emailValidity.value = data.validity;
        }
    }

    const getEmailAddressValidityDebounced = debounce(() => {
        if (v$.value.email.$invalid) {
            emailValidity.value = 'VALID_ADDRESS';
            return;
        }
        getEmailAddressValidity();
    }, 250);

    watch(emailModel, () => {
        emailAddressAlreadyUsedReceivedViaParameters.value = '';
        getEmailAddressValidityDebounced();
    });

    function signupLinkWith(provider: string) {
        return (
            `/registration/oauth2/authorization/${provider}` +
            '?actionType=signup' +
            '&identityType=freelancer' +
            `&state=${encodeURIComponent(resumptionDetails.requestId)}` +
            `&errRedirect=${encodeURIComponent(resumptionDetails.resumptionUrl)}`
        );
    }

    async function submit(): Promise<void> {
        loading.value = true;

        firstNameError.value = '';
        lastNameError.value = '';

        const [, error] = await profileCreationService.createAccountAndProfile(
            {
                firstName: firstNameModel.value,
                lastName: lastNameModel.value,
                email: emailModel.value,
                password: passwordModel.value,
                optOutFromNewsletter: optOutFromNewsletterModel.value,
            },
            resumptionDetails.requestId,
        );

        loading.value = false;

        if (!error) {
            window.location.href = router.resolve({name: route.meta.nextStep as RouteRecordName}).href;
        } else if (error.response?.status === 400) {
            const data = error.response.data;
            if (Array.isArray(data)) {
                data.forEach((d) => {
                    if (!d.attribute) {
                        return;
                    }
                    switch (d.attribute) {
                        case 'firstName':
                            firstNameError.value = data[0].error;
                            break;
                        case 'lastName':
                            lastNameError.value = data[0].error;
                            break;
                    }
                });
            } else if (data.error) {
                switch (data.error) {
                    case ProfileCreationError.EmailAlreadyUsed:
                        errorFormEmailAlreadyUsed.value = true;
                        break;
                    case ProfileCreationError.ProfileAlreadyExists:
                        errorFormEmailAlreadyUsed.value = true;
                        displayExistingProfileJoiningMaltStrategy.value = store.state.isFunnelStrategy;
                        break;
                }
            }
        }
    }

    async function goNext(): Promise<void> {
        nextButtonClicked.value = true;

        if (!isCompletelyValid.value || !agreementModel.value) {
            return;
        }

        await submit();
    }

return (_ctx: any,_cache: any) => {
  const _component_VJoyLink = _resolveComponent("VJoyLink")!
  const _component_VJoyButton = _resolveComponent("VJoyButton")!
  const _component_VJoyFormError = _resolveComponent("VJoyFormError")!
  const _component_VJoySeparator = _resolveComponent("VJoySeparator")!
  const _component_VJoyInput = _resolveComponent("VJoyInput")!
  const _component_VJoyHighlight = _resolveComponent("VJoyHighlight")!
  const _component_v_joy_input = _resolveComponent("v-joy-input")!
  const _component_VJoyCheckbox = _resolveComponent("VJoyCheckbox")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(Step, {
      class: "account-creation",
      "is-step-valid": isCompletelyValid.value,
      onGoToNext: goNext
    }, {
      "step-content": _withCtx(() => [
        _createElementVNode("h2", null, _toDisplayString(_ctx.$msg('profile.creation.completion.wizard.account.title')), 1),
        _createElementVNode("h3", null, [
          _createTextVNode(_toDisplayString(_ctx.$msg('profile.creation.completion.wizard.account.switch.to.signin')) + " ", 1),
          _createVNode(_component_VJoyLink, {
            href: `/signin?redirect=${_unref(resumptionDetails).resumptionUrl}`,
            "data-testid": "account-creation-signin"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$msg('profile.creation.completion.wizard.account.signin.link')), 1)
            ]),
            _: 1
          }, 8, ["href"])
        ]),
        _createElementVNode("div", _hoisted_1, [
          _createVNode(_component_VJoyButton, {
            variant: "secondary",
            href: signupLinkWith('google'),
            "data-testid": "account-creation-google"
          }, {
            default: _withCtx(() => [
              _cache[7] || (_cache[7] = _createElementVNode("img", {
                src: "https://dam.malt.com/social-networks/signin-google",
                alt: "",
                height: "18"
              }, null, -1)),
              _createElementVNode("span", null, _toDisplayString(_ctx.$msg('profile.creation.completion.wizard.account.google')), 1)
            ]),
            _: 1
          }, 8, ["href"]),
          _createVNode(_component_VJoyButton, {
            variant: "secondary",
            href: signupLinkWith('facebook'),
            "data-testid": "account-creation-facebook"
          }, {
            default: _withCtx(() => [
              _cache[8] || (_cache[8] = _createElementVNode("img", {
                src: "https://dam.malt.com/social-networks/signin-facebook",
                alt: "",
                height: "18"
              }, null, -1)),
              _createElementVNode("span", null, _toDisplayString(_ctx.$msg('profile.creation.completion.wizard.account.facebook')), 1)
            ]),
            _: 1
          }, 8, ["href"]),
          (emailAddressAlreadyUsedReceivedViaParameters.value)
            ? (_openBlock(), _createBlock(_component_VJoyFormError, { key: 0 }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$msg('profile.creation.completion.wizard.account.social.signup.error')), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ]),
        _createVNode(_component_VJoySeparator, {
          text: _ctx.$msg('profile.creation.completion.wizard.account.or'),
          size: "small"
        }, null, 8, ["text"]),
        _createVNode(_component_VJoyInput, {
          modelValue: firstNameModel.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((firstNameModel).value = $event)),
          name: "firstName",
          label: _ctx.$msg('profile.creation.completion.wizard.account.firstname.label'),
          placeholder: _ctx.$msg('profile.creation.completion.wizard.account.firstname.placeholder'),
          "data-testid": "account-creation-firstname"
        }, null, 8, ["modelValue", "label", "placeholder"]),
        (firstNameError.value)
          ? (_openBlock(), _createBlock(_component_VJoyFormError, { key: 0 }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(firstNameError.value), 1)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        _createVNode(_component_VJoyInput, {
          modelValue: lastNameModel.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((lastNameModel).value = $event)),
          name: "lastName",
          label: _ctx.$msg('profile.creation.completion.wizard.account.lastname.label'),
          placeholder: _ctx.$msg('profile.creation.completion.wizard.account.lastname.placeholder'),
          "data-testid": "account-creation-lastname"
        }, null, 8, ["modelValue", "label", "placeholder"]),
        (lastNameError.value)
          ? (_openBlock(), _createBlock(_component_VJoyFormError, { key: 1 }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(lastNameError.value), 1)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        _createVNode(_component_VJoyInput, {
          modelValue: emailModel.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((emailModel).value = $event)),
          name: "email",
          type: "email",
          label: _ctx.$msg('profile.creation.completion.wizard.account.email.label'),
          placeholder: "name@work-email.com",
          "data-testid": "account-creation-email"
        }, null, 8, ["modelValue", "label"]),
        (errorFormEmailAlreadyUsed.value)
          ? (_openBlock(), _createBlock(_component_VJoyFormError, { key: 2 }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(msg)('validation.message.email.already.used')), 1)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (emailValidity.value === 'UNCLAIMED_COMATCH_ACCOUNT')
          ? (_openBlock(), _createBlock(_component_VJoyHighlight, {
              key: 3,
              class: "joy-mb-4",
              accent: "",
              "display-icon": "",
              level: "info",
              "data-testid": "account-creation-comatch"
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", {
                  innerHTML: 
                        _ctx.$msg(
                            'profile.creation.completion.wizard.account.unclaimed.comatch.account.message',
                            `/c/comatch/hello?email=${_unref(escapeHtml)(encodeURIComponent(emailModel.value))}`,
                        )
                    
                }, null, 8, _hoisted_2)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (emailValidity.value !== 'UNCLAIMED_COMATCH_ACCOUNT')
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 4 }, [
              _createVNode(_component_v_joy_input, {
                modelValue: passwordModel.value,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((passwordModel).value = $event)),
                class: "password-input",
                label: _ctx.$msg('profile.creation.completion.wizard.account.password.label'),
                placeholder: _ctx.$msg('profile.creation.completion.wizard.account.password.placeholder'),
                autocomplete: "off",
                name: "password",
                type: "password",
                "data-testid": "account-creation-password"
              }, null, 8, ["modelValue", "label", "placeholder"]),
              _createElementVNode("ul", _hoisted_3, [
                _createElementVNode("li", {
                  class: _normalizeClass(["password-constraints__item", {'password-constraints__item--valid': passwordLengthValid.value}])
                }, _toDisplayString(_ctx.$msg('profile.creation.completion.wizard.account.password.constraint.characters.length')), 3),
                _createElementVNode("li", {
                  class: _normalizeClass(["password-constraints__item", {'password-constraints__item--valid': passwordUpperCaseValid.value}])
                }, _toDisplayString(_ctx.$msg('profile.creation.completion.wizard.account.password.constraint.letter.uppercase')), 3),
                _createElementVNode("li", {
                  class: _normalizeClass(["password-constraints__item", {'password-constraints__item--valid': passwordSpecialValid.value}])
                }, _toDisplayString(_ctx.$msg('profile.creation.completion.wizard.account.password.constraint.characters.special')), 3)
              ])
            ], 64))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(_component_VJoyCheckbox, {
            checked: optOutFromNewsletterModel.value,
            "onUpdate:checked": _cache[4] || (_cache[4] = ($event: any) => ((optOutFromNewsletterModel).value = $event))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$msg('profile.creation.completion.wizard.account.optout.checkbox')), 1)
            ]),
            _: 1
          }, 8, ["checked"]),
          _createVNode(_component_VJoyCheckbox, {
            checked: agreementModel.value,
            "onUpdate:checked": _cache[5] || (_cache[5] = ($event: any) => ((agreementModel).value = $event)),
            "data-testid": "account-creation-agreement"
          }, {
            default: _withCtx(() => [
              _createElementVNode("span", {
                innerHTML: _ctx.$msg('profile.creation.completion.wizard.account.tc.checkbox')
              }, null, 8, _hoisted_5)
            ]),
            _: 1
          }, 8, ["checked"]),
          (!agreementModel.value && nextButtonClicked.value)
            ? (_openBlock(), _createBlock(_component_VJoyFormError, {
                key: 0,
                "data-testid": "account-creation-agreement-error"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$msg('profile.creation.completion.wizard.account.tc.error')), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ])
      ]),
      "step-side": _withCtx(() => _cache[9] || (_cache[9] = [
        _createElementVNode("div", { class: "profile-creation-wizard__side" }, null, -1)
      ])),
      _: 1
    }, 8, ["is-step-valid"]),
    (displayExistingProfileJoiningMaltStrategy.value)
      ? (_openBlock(), _createBlock(ExistingProfileJoiningMaltStrategy, {
          key: 0,
          onCancel: _cache[6] || (_cache[6] = ($event: any) => (displayExistingProfileJoiningMaltStrategy.value = false))
        }))
      : _createCommentVNode("", true)
  ], 64))
}
}

})